import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { learningPlanApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, BookMarked, Search } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { LearningPlan, Status } from '@/types/learningManagement';

export const LearningPlanListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'learningPlan');
  const canEdit = hasPermission('edit', 'learningPlan');
  const canDelete = hasPermission('delete', 'learningPlan');

  // Build fetch function with conditional API calls
  const fetchLearningPlansFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return learningPlanApi.search({
          title: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else if (filterStatus !== 'all') {
        return learningPlanApi.listByStatus(filterStatus, { page: params.page, size: params.size });
      } else {
        return learningPlanApi.list({
          page: params.page,
          size: params.size,
          sortBy: 'createdAt',
          direction: 'desc',
        });
      }
    };
  }, [searchQuery, filterStatus]);

  // Use paginated list hook
  const {
    data: learningPlans,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<LearningPlan>({
    fetchFn: fetchLearningPlansFn,
    entityName: 'learning plans',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await learningPlanApi.delete(id);
    },
    entityName: 'learning plan',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this learning plan?')) return;
    await deleteMutation.mutate(id);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: Status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'DEPRECATED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const learningPlanId = (plan: LearningPlan) => plan.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Learning Plan Management</h1>
          <p className="text-muted-foreground mt-2">Manage all learning plans</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/learning-plans/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Learning Plan
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search learning plans by title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
                <SelectItem value="DEPRECATED">Deprecated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Learning Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>Learning Plans ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all learning plans</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading learning plans..." />
          ) : (error || learningPlans.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load learning plans. Please try again or create a new one." : "Get started by creating your first learning plan."}
              icon={BookMarked}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/learning-plans/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Plan Type</TableHead>
                    <TableHead>Learning Plan</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {learningPlans.map((plan) => (
                    <TableRow key={learningPlanId(plan)}>
                      <TableCell className="font-medium">{plan.title}</TableCell>
                      <TableCell>{plan.planType || plan.pathType || '—'}</TableCell>
                      <TableCell>{plan.learningPlan || plan.pathPlan || '—'}</TableCell>
                      <TableCell>{plan.difficultyLevel}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(plan.status)}`}>
                          {plan.status}
                        </span>
                      </TableCell>
                      <TableCell>{(plan.learningPlanGenerated || plan.learningPathGenerated) ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {formatDate(plan.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/learning-plans/${learningPlanId(plan)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/learning-plans/${learningPlanId(plan)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(learningPlanId(plan))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LearningPlanListPage;
